import { useTopRowOverContext } from '@/components/DraggableTable/context/TopRowOverProvider';
import {
  Active,
  AutoScrollActivator,
  DndContext,
  DragEndEvent,
  MouseSensor,
  Over,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { TRecordType } from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useMemo } from 'react';
type Props<RecordType extends TRecordType> = {
  data: RecordType[];
  children: React.ReactNode;
  dataSource: TRecordType[];
  droppedRowId: string | number | null;
  setDroppedRowId: (id: number) => void;
  setDataSource: (
    active: Active,
    over: Over | null,
    isShowTop: boolean
  ) => void;
  onDragStart: () => void;
};

export const DraggableTable: FC<Props<TRecordType>> = memo(
  ({ children, setDataSource, dataSource, onDragStart }) => {
    const { isShowTop } = useTopRowOverContext();
    const onDragEnd = ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        setDataSource(active, over, isShowTop);
      }
    };

    const sensors = useSensors(
      useSensor(MouseSensor, {
        activationConstraint: { distance: 5 },
      })
    );

    const canScroll = useCallback((element: Element) => {
      return element.classList.contains('rci-table-body');
    }, []);

    const autoScroll = useMemo(() => {
      return {
        threshold: {
          x: 0.2,
          y: 0.02,
        },
        acceleration: 20,
        interval: 5,
        enabled: true,
        activator: AutoScrollActivator.DraggableRect,
        canScroll,
      };
    }, [canScroll]);

    return (
      <DndContext
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        sensors={sensors}
        autoScroll={autoScroll}
      >
        <SortableContext items={dataSource.map((i) => ({ id: i.id }))}>
          {children}
        </SortableContext>
      </DndContext>
    );
  }
);
DraggableTable.displayName = 'DraggableTable';
